import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, of, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AplicacaoMenuResponse } from '../models/application-menu.models';

@Injectable({ providedIn: 'root' })
export class PermissaoService {
  private readonly http = inject(HttpClient);

  /** Chaves autorizadas (ex: "logistica.remessas:find_all") */
  private chavesAutorizadas = signal<Set<string>>(new Set());

  /** Indica se as permissões já foram carregadas */
  carregado = signal(false);

  /**
   * Busca as permissões do SGI e armazena as chaves autorizadas.
   * Chame isso uma única vez no bootstrap da app (ex: APP_INITIALIZER ou no layout principal).
   */
  carregarPermissoes(aplicacaoId: string): Observable<AplicacaoMenuResponse | null> {
    const url = `${environment.apiUrl}/aplicacoes/${aplicacaoId}/menu`;

    return this.http.get<AplicacaoMenuResponse>(url).pipe(
      tap((response) => {
        const chaves = new Set<string>();

        if (response && response.recursos) {
          for (const recurso of response.recursos) {
            if (recurso.funcionalidades) {
              for (const func of recurso.funcionalidades) {
                if (func.autorizado) {
                  chaves.add(func.chave);
                }
              }
            }
          }
        }
        this.chavesAutorizadas.set(chaves);
        this.carregado.set(true);
      }),
      catchError((_err) => {
        this.carregado.set(true); // Evita travar a app se o initializer esperar
        return of(null);
      })
    );
  }

  /** Verifica se o usuário tem UMA permissão específica */
  hasPermission(chave: string): boolean {
    const has = this.chavesAutorizadas().has(chave);
    if (!has && !environment.production) {
      console.warn(`[PermissaoService] Acesso negado para a chave: ${chave}`);
    }
    return has;
  }

  /** Verifica se o usuário tem QUALQUER UMA das permissões informadas */
  hasAnyPermission(chaves: string[]): boolean {
    if (chaves.length === 0) {
      return true;
    }
    const has = chaves.some((c) => this.chavesAutorizadas().has(c));
    if (!has && chaves.length > 0 && !environment.production) {
      console.warn(`[PermissaoService] Acesso negado para qualquer uma das chaves:`, chaves);
    }
    return has;
  }

  /** Verifica se o usuário tem TODAS as permissões informadas */
  hasAllPermissions(chaves: string[]): boolean {
    return chaves.every((c) => this.chavesAutorizadas().has(c));
  }
}